/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import * as AppointmentActions from '@appointments/store/appointment.actions';
import { State } from '@core/store';
import { Store } from '@ngrx/store';
import { Tour, TourClass } from '@shared/models';
import { TourView } from '@tours/models/tour-view';
import { ToursOverviewTab } from '@tours/models/tours-overview-tab';
import * as TourActions from '@tours/store/tours.actions';
import {
  getCurrentDate,
  getCurrentTourClass,
  getCurrentTourView,
  getReturns,
  getTours,
  isActionPending,
  isSidebarExpanded
} from '@tours/store/tours.selectors';
import { SplitAreaSize } from 'angular-split';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, tap, withLatestFrom } from 'rxjs/operators';

@Component({
    selector: 'app-tours',
    templateUrl: './tours.component.html',
    styleUrls: ['./tours.component.scss'],
    standalone: false
})
export class ToursComponent implements OnInit, OnDestroy {
  tourView = TourView;

  tours$: Observable<Tour[]>;
  returns$: Observable<Tour[]>;
  isSidebarExpanded$: Observable<boolean>;
  currentDate$: Observable<Date>;
  isActionPending$: Observable<boolean>;
  currentView$: Observable<TourView>;
  currentTab$: Observable<number>;

  sizes = {
    defaultTourList: 70,
    returnTourList: 30,
  };

  private tourViewSubscription: Subscription;

  constructor(private store: Store<State>) { }

  ngOnInit(): void {
    this.tours$ = this.store.select(getTours);
    this.returns$ = this.store.select(getReturns);
    this.isSidebarExpanded$ = this.store.select(isSidebarExpanded);
    this.currentDate$ = this.store.select(getCurrentDate);
    this.isActionPending$ = this.store.select(isActionPending);
    this.currentView$ = this.store.select(getCurrentTourView);
    this.currentTab$ = this.store.select(getCurrentTourClass).pipe(
      withLatestFrom(this.store.select(getCurrentTourView)),
      map(([tourClass, view]) => {
        if (view === TourView.Appointments) {
          return ToursOverviewTab.Appointments;
        }
        switch (tourClass) {
          case TourClass.Delivery:
            return ToursOverviewTab.DeliveryTours;
          case TourClass.Maintenance:
            return ToursOverviewTab.MaintenanceTours;
          case TourClass.Service:
            return ToursOverviewTab.ServiceTours;
          default:
            return ToursOverviewTab.AllTours;
        }
      })
    );

    this.tourViewSubscription = this.store.select(getCurrentTourView).pipe(
      distinctUntilChanged(),
      tap((view: TourView) => {
        if (view === TourView.Appointments) {
          this.store.dispatch(new AppointmentActions.LoadAppointments());
        } else {
          this.store.dispatch(new TourActions.LoadTours());
        }
      })
    ).subscribe();

    if (null !== localStorage.getItem('tourListSizes')) {
      this.sizes = JSON.parse(localStorage.getItem('tourListSizes'));
    }
  }

  ngOnDestroy(): void {
    if (this.tourViewSubscription) {
      this.tourViewSubscription.unsubscribe();
    }
  }

  tabSelectionChanged(tabIndex: number): void {
    if (tabIndex === ToursOverviewTab.Appointments) {
      this.store.dispatch(new TourActions.SetCurrentView(TourView.Appointments));
    } else {
      let tourClass: TourClass;
      switch (tabIndex) {
        case ToursOverviewTab.DeliveryTours:
          tourClass = TourClass.Delivery;
          break;
        case ToursOverviewTab.MaintenanceTours:
          tourClass = TourClass.Maintenance;
          break;
        case ToursOverviewTab.ServiceTours:
          tourClass = TourClass.Service;
          break;
        default:
          tourClass = TourClass.All;
      }
      this.store.dispatch(new TourActions.SetTourFilter(tourClass));
      this.store.dispatch(new TourActions.SetCurrentView(TourView.Tours));
    }
  }

  toggleSidebar(): void {
    this.store.dispatch(new TourActions.ToggleSidebar());
  }

  viewPreviousDay(): void {
    this.store.dispatch(new TourActions.DecrementCurrentDate());
  }

  viewNextDay(): void {
    this.store.dispatch(new TourActions.IncrementCurrentDate());
  }

  dragEnd(sizes: SplitAreaSize[]): void {
    this.sizes.defaultTourList = Number(sizes[0]);
    this.sizes.returnTourList = Number(sizes[1]);
    localStorage.setItem('tourListSizes', JSON.stringify(this.sizes));
  }
}
